import { clampPct } from '@hakimo-ui/hakimo/ui-elements';
import { atom } from 'jotai';
import { RefObject, useEffect, useState, useSyncExternalStore } from 'react';

export const SOP_WIDTH_DEFAULT_PCT = 25;

const MIN_VIDEO_COLUMN_PX = 441;

const FULL_SCREEN = { minPct: 20, maxPct: 40 } as const;
const SPLIT_SCREEN = { minPct: 25, maxPct: 50 } as const;
const SPLIT_SCREEN_MAX_WIDTH_PX = 1100;

const subscribeToResize = (onChange: () => void) => {
  window.addEventListener('resize', onChange);
  return () => window.removeEventListener('resize', onChange);
};

const getRangeSnapshot = () =>
  window.innerWidth < SPLIT_SCREEN_MAX_WIDTH_PX ? SPLIT_SCREEN : FULL_SCREEN;

export function useSopWidthRange(containerRef: RefObject<HTMLElement | null>) {
  const range = useSyncExternalStore(subscribeToResize, getRangeSnapshot);
  const [containerWidth, setContainerWidth] = useState(0);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const observer = new ResizeObserver(() =>
      setContainerWidth(el.clientWidth)
    );
    observer.observe(el);
    setContainerWidth(el.clientWidth);
    return () => observer.disconnect();
  }, [containerRef]);

  const pixelCapPct = containerWidth
    ? ((containerWidth - MIN_VIDEO_COLUMN_PX) / containerWidth) * 100
    : range.maxPct;

  return {
    minPct: range.minPct,
    maxPct: clampPct(pixelCapPct, range.minPct, range.maxPct),
  };
}

const STORAGE_KEY = 'umSopPanelWidthPct';

function readStoredSopWidth(): number {
  try {
    const stored = Number(localStorage.getItem(STORAGE_KEY));
    return stored > 0 ? stored : SOP_WIDTH_DEFAULT_PCT;
  } catch {
    return SOP_WIDTH_DEFAULT_PCT;
  }
}

const widthAtom = atom(readStoredSopWidth());

export const sopPanelWidthPctAtom = atom(
  (get) => get(widthAtom),
  (_get, set, next: number) => {
    set(widthAtom, next);
    try {
      localStorage.setItem(STORAGE_KEY, String(next));
    } catch {
      // The width still applies for this session, it just isn't remembered.
    }
  }
);
